import React, { useState } from "react";
import { Box, Chip, IconButton, Tooltip, Typography } from "@mui/joy";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import AutoStoriesOutlinedIcon from "@mui/icons-material/AutoStoriesOutlined";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import LinkIcon from "@mui/icons-material/Link";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import remarkGfm from "remark-gfm";
import { SectionCitation } from "../../types/citations";
import styles from "./SourcePanel.module.css";

interface ReferenceItemProps {
  citation: SectionCitation;
  isExpanded: boolean;
  onClick: () => void;
  onClickInEditor: () => void;
  onViewPage?: (sourceDocumentId: number, page: number) => void;
  onScrollToSection?: (sectionId: number) => void;
}

const getSourceTypeIcon = (sourceType: string) => {
  switch (sourceType) {
    case "Example":
      return <AutoStoriesOutlinedIcon sx={{ fontSize: 14 }} />;
    case "Guidance":
      return <LightbulbOutlinedIcon sx={{ fontSize: 14 }} />;
    case "Internal":
      return <LinkIcon sx={{ fontSize: 14 }} />;
    default:
      return <DescriptionOutlinedIcon sx={{ fontSize: 14 }} />;
  }
};

const isPdfSource = (filename: string) =>
  filename?.toLowerCase().endsWith(".pdf");

const ReferenceItem: React.FC<ReferenceItemProps> = ({
  citation,
  isExpanded,
  onClick,
  onClickInEditor,
  onViewPage,
  onScrollToSection,
}) => {
  const isSourceType = citation.source_type === "Source";
  const isInternal = citation.source_type === "Internal";
  const isVerified = !isSourceType || citation.verified;
  const [showFullContext, setShowFullContext] = useState(false);
  const canViewPage =
    citation.page != null &&
    citation.source_document_id != null &&
    isPdfSource(citation.source_filename) &&
    !!onViewPage;

  // Use highlighted_context (from Haiku) if available, otherwise fall back to surrounding_context
  const displayContext =
    citation.highlighted_context || citation.surrounding_context;
  const hasContext = !!displayContext?.trim();

  return (
    <div
      className={`${styles.referenceItem} ${isExpanded ? styles.referenceItemExpanded : ""}`}
    >
      {/* Collapsed header */}
      <div
        className={styles.referenceItemHeader}
        onClick={onClick}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") onClick();
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.75,
            flex: 1,
            minWidth: 0,
          }}
        >
          <Chip
            size="sm"
            variant="soft"
            color={isVerified ? "success" : "warning"}
            sx={{ fontWeight: 600, fontSize: "0.7rem", flexShrink: 0 }}
          >
            {citation.ref_id}
          </Chip>
          {getSourceTypeIcon(citation.source_type)}
          <Typography
            level="body-xs"
            sx={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              flex: 1,
              minWidth: 0,
            }}
          >
            {isInternal
              ? citation.reference_section_title || "Internal ref"
              : citation.source_filename}
            {!isInternal && citation.page != null && (
              <Typography
                component="span"
                level="body-xs"
                sx={{ color: "neutral.600", fontWeight: 600 }}
              >
                {" "}
                p.{citation.page}
              </Typography>
            )}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
          {isSourceType || isInternal ? (
            <Tooltip
              title={
                citation.verification_reason ||
                (citation.verdict === "acceptable_variation"
                  ? "Acceptable variation"
                  : isVerified
                    ? "Verified"
                    : "Unverified")
              }
              placement="left"
              size="sm"
            >
              {citation.verdict === "acceptable_variation" ? (
                <InfoOutlinedIcon sx={{ fontSize: 16, color: "primary.500" }} />
              ) : isVerified ? (
                <CheckCircleOutlineIcon
                  sx={{ fontSize: 16, color: "success.500" }}
                />
              ) : (
                <WarningAmberIcon sx={{ fontSize: 16, color: "warning.500" }} />
              )}
            </Tooltip>
          ) : (
            <CheckCircleOutlineIcon
              sx={{ fontSize: 16, color: "neutral.400" }}
            />
          )}
        </Box>
      </div>

      {/* Expanded body */}
      {isExpanded && (
        <div className={styles.referenceItemBody}>
          {/* Source file + page / Internal section ref */}
          <Box
            sx={{
              mb: 1,
              display: "flex",
              alignItems: "center",
              gap: 1,
              flexWrap: "wrap",
            }}
          >
            <Typography level="body-xs" fontWeight="lg">
              {isInternal
                ? citation.reference_section_title || "Internal reference"
                : citation.source_filename}
            </Typography>
            {isInternal && citation.reference_section_id != null && (
              <Chip
                size="sm"
                variant="solid"
                color="primary"
                sx={{
                  fontSize: "0.65rem",
                  cursor: "pointer",
                  gap: 0.25,
                }}
                endDecorator={<LinkIcon sx={{ fontSize: 12 }} />}
                onClick={(e) => {
                  e.stopPropagation();
                  onScrollToSection?.(citation.reference_section_id!);
                }}
              >
                Section
              </Chip>
            )}
            {!isInternal && citation.page != null && (
              <Chip
                size="sm"
                variant={canViewPage ? "solid" : "soft"}
                color={canViewPage ? "primary" : "neutral"}
                sx={{
                  fontSize: "0.65rem",
                  cursor: canViewPage ? "pointer" : "default",
                  gap: 0.25,
                }}
                endDecorator={
                  canViewPage ? (
                    <OpenInNewIcon sx={{ fontSize: 12 }} />
                  ) : undefined
                }
                onClick={
                  canViewPage
                    ? (e) => {
                        e.stopPropagation();
                        onViewPage!(
                          citation.source_document_id!,
                          citation.page!,
                        );
                      }
                    : undefined
                }
              >
                Page {citation.page}
              </Chip>
            )}
            <Chip
              size="sm"
              variant="outlined"
              color="neutral"
              sx={{ fontSize: "0.65rem" }}
            >
              {citation.source_type}
            </Chip>
          </Box>

          {/* Exact passage - always visible */}
          <Box
            sx={{
              backgroundColor: isVerified
                ? "rgba(59, 130, 246, 0.08)"
                : "rgba(245, 158, 11, 0.12)",
              borderLeft: isVerified
                ? "3px solid rgba(59, 130, 246, 0.5)"
                : "3px solid rgba(245, 158, 11, 0.5)",
              px: 1.5,
              py: 1,
              my: 0.5,
              borderRadius: "0 4px 4px 0",
            }}
          >
            <Typography level="body-xs" sx={{ lineHeight: 1.6 }}>
              {citation.exact_passage}
            </Typography>
          </Box>
          {citation.verification_reason && (
            <Typography
              level="body-xs"
              sx={{
                color: isVerified ? "success.600" : "warning.600",
                lineHeight: 1.4,
                mt: 0.5,
                fontSize: "0.7rem",
                fontStyle: "italic",
              }}
            >
              {citation.verification_reason}
            </Typography>
          )}

          {/* Full source context (collapsible, rendered as markdown) */}
          {hasContext && (
            <>
              <Box sx={{ display: "flex", justifyContent: "center", mt: 0.5 }}>
                <IconButton
                  size="sm"
                  variant="plain"
                  color="neutral"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowFullContext(!showFullContext);
                  }}
                  sx={{ fontSize: "0.7rem", gap: 0.5, py: 0 }}
                >
                  {showFullContext ? (
                    <ExpandLessIcon sx={{ fontSize: 16 }} />
                  ) : (
                    <ExpandMoreIcon sx={{ fontSize: 16 }} />
                  )}
                  <Typography level="body-xs" sx={{ color: "neutral.500" }}>
                    {showFullContext ? "Hide" : "Show"} source context
                  </Typography>
                </IconButton>
              </Box>
              {showFullContext && (
                <Box
                  sx={{
                    mt: 0.5,
                    fontSize: "0.75rem",
                    lineHeight: 1.6,
                    color: "neutral.700",
                    "& mark": {
                      backgroundColor: isVerified
                        ? "rgba(59, 130, 246, 0.2)"
                        : "rgba(245, 158, 11, 0.25)",
                      borderRadius: "2px",
                      px: "2px",
                    },
                    "& p": { my: 0.5 },
                    "& h1, & h2, & h3, & h4, & h5, & h6": {
                      fontSize: "0.8rem",
                      fontWeight: 700,
                      mt: 1,
                      mb: 0.5,
                    },
                    "& ul, & ol": { pl: 2, my: 0.5 },
                    "& table": {
                      fontSize: "0.7rem",
                      borderCollapse: "collapse",
                      width: "100%",
                      my: 0.5,
                    },
                    "& th, & td": {
                      border: "1px solid",
                      borderColor: "neutral.300",
                      px: 0.75,
                      py: 0.25,
                    },
                    "& th": { fontWeight: 600, bgcolor: "neutral.100" },
                    "& code": {
                      fontSize: "0.7rem",
                      bgcolor: "neutral.100",
                      px: 0.5,
                      borderRadius: "2px",
                    },
                  }}
                >
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    rehypePlugins={[rehypeRaw]}
                  >
                    {displayContext}
                  </ReactMarkdown>
                </Box>
              )}
            </>
          )}

          {/* Show in document link */}
          <Box sx={{ mt: 1, display: "flex", justifyContent: "flex-end" }}>
            <Typography
              level="body-xs"
              role="button"
              tabIndex={0}
              sx={{
                color: "primary.500",
                cursor: "pointer",
                "&:hover": { textDecoration: "underline" },
              }}
              onClick={(e) => {
                e.stopPropagation();
                onClickInEditor();
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  e.stopPropagation();
                  onClickInEditor();
                }
              }}
            >
              Show in document
            </Typography>
          </Box>
        </div>
      )}
    </div>
  );
};

export default React.memo(ReferenceItem);
